import { useState, ReactNode, useCallback } from 'react'
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import {
  Badge,
  Box,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadIcon from '@mui/icons-material/Download'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import CancelIcon from '@mui/icons-material/Cancel'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'

const XlsDownloadToolbar = ({
  fileName,
}: {
  fileName: string
  children?: ReactNode
}) => {
  const apiRef = useGridApiContext() as React.RefObject<GridApiPremium>
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = () => {
    setIsDownloading(true)
    apiRef.current.exportDataAsExcel({ fileName })
    setIsDownloading(false)
  }

  const filterPanelRender = useCallback(
    (props: any, state: any) => (
      <ToolbarButton {...props} color="default">
        <Badge badgeContent={state.filterCount} color="primary" variant="dot">
          <FilterListIcon fontSize="small" />
        </Badge>
      </ToolbarButton>
    ),
    []
  )

  const quickFilterTriggerRender = useCallback(
    (triggerProps: any, state: { expanded: boolean }) => (
      <Tooltip title="Search" enterDelay={0}>
        <ToolbarButton
          {...triggerProps}
          color="default"
          aria-disabled={state.expanded}
          sx={{
            opacity: state.expanded ? 0 : 1,
            pointerEvents: state.expanded ? 'none' : 'auto',
            position: state.expanded ? 'absolute' : 'relative',
          }}
        >
          <SearchIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>
    ),
    []
  )

  const quickFilterControlRender = useCallback(
    (
      { ref, ...controlProps }: any,
      state: { expanded: boolean; value: string }
    ) => (
      <TextField
        {...controlProps}
        inputRef={ref}
        aria-label="Search"
        placeholder="Search..."
        size="small"
        sx={{
          width: state.expanded ? 260 : 0,
          opacity: state.expanded ? 1 : 0,
          transition: 'width 0.2s ease, opacity 0.2s ease',
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: state.value ? (
              <InputAdornment position="end">
                <QuickFilterClear
                  edge="end"
                  size="small"
                  aria-label="Clear search"
                  material={{ sx: { marginRight: -0.75 } }}
                >
                  <CancelIcon fontSize="small" />
                </QuickFilterClear>
              </InputAdornment>
            ) : null,
            ...controlProps.slotProps?.input,
          },
          ...controlProps.slotProps,
        }}
      />
    ),
    []
  )

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: theme.palette.grey[100],
      })}
    >
      <Toolbar>
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          sx={{ width: '100%' }}
        >
          <Stack display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Tooltip title="Columns">
              <ColumnsPanelTrigger render={<ToolbarButton />}>
                <ViewColumnIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </Tooltip>

            <Tooltip title="Filters">
              <FilterPanelTrigger render={filterPanelRender} />
            </Tooltip>

            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

            <LoadingButton
              loading={isDownloading}
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              loadingPosition="start"
            >
              <span>Download</span>
            </LoadingButton>

            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

            <QuickFilter debounceMs={300}>
              <QuickFilterTrigger render={quickFilterTriggerRender} />
              <QuickFilterControl render={quickFilterControlRender} />
            </QuickFilter>
          </Stack>
        </Stack>
      </Toolbar>
    </Box>
  )
}

// Stable component factories — created once at module level to avoid remounting
const DetailToolbar = () => (
  <XlsDownloadToolbar fileName="WarehouseInboundForecastReport" />
)
const SummaryToolbar = () => (
  <XlsDownloadToolbar fileName="WarehouseInboundForecastSummaryReport" />
)

export const detailXlsToolbar = () => DetailToolbar
export const summaryXlsToolbar = () => SummaryToolbar
